import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { MiningAccount, loadBalancerAPI } from '../../../api/load-balancer';
import { useTranslation } from 'react-i18next';

export enum AddEditAccountModalType {
  Add = 'add',
  Edit = 'edit',
}

type AddEditAccountModalProps = {
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
} & (
  | {
      miningAccount: MiningAccount;
      type: AddEditAccountModalType.Edit;
    }
  | {
      miningAccount?: undefined;
      type: AddEditAccountModalType.Add;
    }
);

export const AddEditAccountModal = ({ miningAccount, type, onClose, onSuccess, onError }: AddEditAccountModalProps) => {
  const { mutate: mutateCreateMiningAccount, isLoading: isLoadingCreateMiningAccount } =
    loadBalancerAPI.hookWrapperMutation.useCreateMiningAccount();
  const { mutate: mutateUpdateMiningAccount, isLoading: isLoadingUpdateMiningAccount } =
    loadBalancerAPI.hookWrapperMutation.useUpdateMiningAccount();
  const isApiLoading = isLoadingCreateMiningAccount || isLoadingUpdateMiningAccount;
  const { t } = useTranslation();

  const validationSchema = yup.object({
    name: yup.string().trim().required(t('addEditAccountModalProps.accountNameIsRequired')),
    description: yup.string().optional(),
  });

  const formik = useFormik({
    initialValues: {
      name: type === AddEditAccountModalType.Edit ? (miningAccount?.name ?? '') : '',
      description: type === AddEditAccountModalType.Edit ? (miningAccount?.description ?? '') : '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (isApiLoading) return;

      const payload = { name: values.name.trim(), description: values.description.trim() };
      if (type === AddEditAccountModalType.Add) {
        mutateCreateMiningAccount(payload, {
          onSuccess: () => {
            onSuccess(t('addEditAccountModalProps.createAccountSuccessfully'));
            onClose();
          },
          onError: (error) => {
            onError(error.message);
          },
        });
      } else {
        if (!miningAccount) {
          onError(t('addEditAccountModalProps.accountNotFound'));
          return;
        }

        mutateUpdateMiningAccount(
          { id: miningAccount.id, ...payload },
          {
            onSuccess: () => {
              onSuccess(t('addEditAccountModalProps.updateAccountSuccessfully'));
              onClose();
            },
            onError: (error) => {
              onError(error.message);
            },
          }
        );
      }
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        component: 'form',
        onSubmit: formik.handleSubmit,
        style: { maxWidth: '500px', width: '100%' },
      }}
    >
      <DialogTitle>
        {type === AddEditAccountModalType.Add ? t('addEditAccountModalProps.add') : t('addEditAccountModalProps.edit')}{' '}
        {t('addEditAccountModalProps.account')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ py: 4 }}>
        <TextField
          id="name"
          name="name"
          label={t('addEditAccountModalProps.tagName')}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          fullWidth
          size="small"
          variant="outlined"
          sx={{ mb: 2 }}
        />
        <TextField
          id="description"
          name="description"
          label={t('addEditAccountModalProps.description')}
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.description && Boolean(formik.errors.description)}
          helperText={formik.touched.description && formik.errors.description}
          fullWidth
          size="small"
          variant="outlined"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="info" size="small">
          {t('addEditAccountModalProps.cancel')}
        </Button>
        <Button type="submit" variant="contained" size="small" disabled={isApiLoading}>
          {t('addEditAccountModalProps.submit')}
        </Button>
      </DialogActions>
      {isApiLoading && <LoadingOverlay />}
    </Dialog>
  );
};
